import React, { useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';

const UploadButton = styled.button`
  background-color: #333;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  
  &:hover {
    background-color: #555;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const FileUpload = ({ setSvgCode }) => {
  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  // Memoize processSvgContent with useCallback
  const processSvgContent = useCallback((content) => {
    try {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(content, 'image/svg+xml');
      const svgElement = svgDoc.querySelector('svg');
      
      if (svgElement) {
        // Ensure the SVG has width and height attributes
        if (!svgElement.hasAttribute('width') || !svgElement.hasAttribute('height')) {
          // Get viewBox values or set defaults
          let viewBox = svgElement.getAttribute('viewBox');
          let width = 400;
          let height = 400;
          
          if (viewBox) {
            const viewBoxValues = viewBox.split(' ').map(Number);
            if (viewBoxValues.length === 4) {
              width = viewBoxValues[2];
              height = viewBoxValues[3];
            }
          } else {
            // If no viewBox, create one
            viewBox = `0 0 ${width} ${height}`;
            svgElement.setAttribute('viewBox', viewBox);
          }
          
          // Set width and height attributes
          svgElement.setAttribute('width', width);
          svgElement.setAttribute('height', height);
        }
        
        // Ensure the SVG has a viewBox if it has width and height
        if (!svgElement.hasAttribute('viewBox') && 
            svgElement.hasAttribute('width') && 
            svgElement.hasAttribute('height')) {
          const width = svgElement.getAttribute('width');
          const height = svgElement.getAttribute('height');
          svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
        }
        
        // Convert the SVG back to a string
        const serializer = new XMLSerializer();
        const processedSvg = serializer.serializeToString(svgDoc);
        
        // Update the SVG code
        setSvgCode(processedSvg);
      } else {
        alert('Invalid SVG structure.');
      }
    } catch (error) {
      console.error('Error processing SVG:', error);
      // If processing fails, use the original content
      setSvgCode(content);
    }
  }, [setSvgCode]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Check if it's an SVG file
      if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const content = event.target.result;
          if (content.includes('<svg')) {
            processSvgContent(content);
          } else {
            alert('The selected file does not contain valid SVG content.');
          }
        };
        reader.readAsText(file);
      } else {
        alert('Please select an SVG file.');
      }
      
      // Reset the input so the same file can be selected again
      e.target.value = '';
    }
  };

  // Add clipboard paste support
  useEffect(() => {
    const handlePaste = (e) => {
      // Check for clipboard data
      const clipboardData = e.clipboardData || window.clipboardData;
      const pastedText = clipboardData.getData('text');
      
      // Check if it's SVG content
      if (pastedText && pastedText.trim().startsWith('<svg') && pastedText.includes('</svg>')) {
        processSvgContent(pastedText);
        e.preventDefault();
      }
    };
    
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [processSvgContent]);

  return (
    <>
      <UploadButton onClick={handleButtonClick}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        Upload SVG
      </UploadButton>
      <HiddenInput 
        type="file" 
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".svg,image/svg+xml"
      />
    </>
  );
};

export default FileUpload; 